import {
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Modal,
    Paper,
    Select,
    Slide,
    TextField,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import React, { useEffect, useMemo, useState } from 'react'
import { Container, Selector, Swiper, TimelinePicker } from '../../components'
import { useDateRange, useSelectVendorService } from '../../hooks'
import {
    ListVendor,
    Schedule,
    Service,
    User,
    WorkorderType,
} from '../../models'
import { InventoryInspectionAnalytic } from './InventoryInspectionAnalyticsBlanket'
import { AssignUnitDict, CreateWorkOrderRequest } from '../../store'
import { typographyStyle } from '../../theme/typographyStyle'
import { fromPickerDate } from '../../helpers'
import { useTimezone } from '../../contexts/timezone'

interface Props {
    open: boolean
    serviceList: Service[]
    vendorList: ListVendor[]
    userList: User[]
    inventoryInspections: InventoryInspectionAnalytic[]
    loading: boolean
    scheduleList: Schedule[]
    initialTitle?: string
    handleClose: () => void
    onClickCreate: (request: CreateWorkOrderRequest) => void
}

export const InventoryInspectionWorkorderModal = (props: Props) => {
    const {
        open,
        serviceList,
        vendorList,
        userList,
        inventoryInspections,
        initialTitle,
        scheduleList,
        loading,
        handleClose,
        onClickCreate,
    } = props

    const [title, setTitle] = useState(initialTitle ? initialTitle : '')
    const [swiperState, setSwiperState] = useState({
        position: ASSIGN_TO_COMPANY,
        companyId: -1,
        userId: -1,
    })

    const [priority, setPriority] = useState(false)
    const [doValidate, setDoValidate] = useState(false)

    const {
        selectService,
        selectVendor,
        selectedServiceId,
        selectedVendorId,
    } = useSelectVendorService(vendorList)

    const { dateRange, setEndDate, setStartDate } = useDateRange({
        rangeKey: 'inventoryInspectionWorkorderModalDateRange',
    })

    const [selectedScheduleId, setSelectedScheduleId] = useState(-1)

    const theme = useTheme()
    const { currentTimezone } = useTimezone()

    useEffect(() => {
        setTitle(initialTitle ? initialTitle : '')
        setPriority(false)
        setSwiperState({
            position: ASSIGN_TO_COMPANY,
            companyId: -1,
            userId: -1,
        })
        setDoValidate(false)
        selectService(-1)
        setStartDate(new Date())
        setEndDate(new Date())
        setSelectedScheduleId(-1)
    }, [open])

    const validVendors = useMemo(() => {
        if (selectedServiceId === -1) {
            return vendorList
        }
        return vendorList.filter((vendor) => {
            return vendor.services.some(
                (service) => service.id === selectedServiceId,
            )
        })
    }, [selectedServiceId, vendorList])

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        display: 'flex',
                        width: 600,
                        borderRadius: 12,
                        overflow: 'hidden',
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'column',
                            flex: 1,
                        }}
                    >
                        {/* Header */}
                        <Container
                            style={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: theme.spacing(2),
                                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 600,
                                }}
                            >
                                Create Workorder
                            </span>
                            <IconButton size="small" onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </Container>

                        {/* Content */}
                        <Container
                            style={{
                                flexDirection: 'column',
                                padding: theme.spacing(2),
                                gap: theme.spacing(2),
                            }}
                        >
                            <TextField
                                label="Workorder Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                                fullWidth
                                variant="outlined"
                                size="small"
                                error={doValidate && title === ''}
                                helperText={
                                    doValidate && title === ''
                                        ? 'Title is required'
                                        : ''
                                }
                            />

                            <TimelinePicker
                                startDate={dateRange.startDate}
                                endDate={dateRange.endDate}
                                onChangeStartDate={setStartDate}
                                onChangeEndDate={setEndDate}
                            />

                            <Container style={{ gap: theme.spacing(2) }}>
                                <Selector
                                    label="Service"
                                    data={serviceList}
                                    searchable
                                    currentValue={selectedServiceId}
                                    onChange={(e) => {
                                        selectService(e.target.value as number)
                                    }}
                                    getDisplayString={(service) => service.name}
                                    customStyle={{
                                        formControl: { flex: 1, margin: 0 },
                                    }}
                                    variant="outlined"
                                    size="small"
                                />
                                <Selector
                                    label="Project"
                                    data={scheduleList}
                                    searchable
                                    currentValue={selectedScheduleId}
                                    onChange={(e) => {
                                        setSelectedScheduleId(
                                            e.target.value as number,
                                        )
                                    }}
                                    getDisplayString={(schedule) =>
                                        schedule.name
                                    }
                                    customStyle={{
                                        formControl: { flex: 1, margin: 0 },
                                    }}
                                    variant="outlined"
                                    size="small"
                                />
                            </Container>

                            <Container
                                style={{
                                    flexDirection: 'column',
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    borderRadius: 8,
                                }}
                            >
                                <Swiper
                                    width={565}
                                    style={{ overflow: 'hidden', margin: 4 }}
                                    position={swiperState.position}
                                    onChangePosition={() => {
                                        setSwiperState({
                                            ...swiperState,
                                            position: !swiperState.position,
                                        })
                                    }}
                                    leftItem={
                                        <FormControl fullWidth>
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                My Vendors
                                            </span>
                                            <Select
                                                value={swiperState.companyId}
                                                variant="outlined"
                                                onChange={(e) => {
                                                    setSwiperState({
                                                        ...swiperState,
                                                        companyId: Number(
                                                            e.target.value,
                                                        ),
                                                    })
                                                }}
                                            >
                                                <MenuItem value={-1}>
                                                    <span
                                                        style={{
                                                            color:
                                                                theme.palette
                                                                    .grey[500],
                                                        }}
                                                    >
                                                        Not Assigned
                                                    </span>
                                                </MenuItem>
                                                {validVendors.map((vnd) => (
                                                    <MenuItem
                                                        key={`SELECT_VENDOR_${vnd.id}`}
                                                        value={vnd.id}
                                                    >
                                                        {vnd.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    }
                                    rightItem={
                                        <FormControl fullWidth>
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                My Team
                                            </span>
                                            <Select
                                                value={swiperState.userId}
                                                variant="outlined"
                                                onChange={(e) => {
                                                    setSwiperState({
                                                        ...swiperState,
                                                        userId: Number(
                                                            e.target.value,
                                                        ),
                                                    })
                                                }}
                                            >
                                                <MenuItem value={-1}>
                                                    <span
                                                        style={{
                                                            color:
                                                                theme.palette
                                                                    .grey[500],
                                                        }}
                                                    >
                                                        Not Assigned
                                                    </span>
                                                </MenuItem>
                                                {userList.map((user) => (
                                                    <MenuItem
                                                        key={`SELECT_USER_${user.id}`}
                                                        value={user.id}
                                                    >
                                                        {user.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    }
                                />
                            </Container>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={priority}
                                        onChange={() => setPriority(!priority)}
                                    />
                                }
                                label="Priority"
                            />
                        </Container>

                        {/* Footer */}
                        <Divider />
                        <Container
                            style={{
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                padding: theme.spacing(2),
                                gap: theme.spacing(1),
                            }}
                        >
                            <Button
                                onClick={handleClose}
                                variant="outlined"
                                style={{ textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={loading}
                                variant="contained"
                                color="primary"
                                style={{ textTransform: 'none', minWidth: 100 }}
                                onClick={() => {
                                    if (title === '') {
                                        setDoValidate(true)
                                        return
                                    }
                                    const startDateIso = dateRange.startDate
                                        ? fromPickerDate(
                                              dateRange.startDate,
                                              currentTimezone,
                                          )
                                              .startOf('day')
                                              .toUTC()
                                              .toISO()
                                        : undefined
                                    const endDateIso = dateRange.endDate
                                        ? fromPickerDate(
                                              dateRange.endDate,
                                              currentTimezone,
                                          )
                                              .endOf('day')
                                              .toUTC()
                                              .toISO()
                                        : undefined

                                    const request: CreateWorkOrderRequest = {
                                        body: {
                                            title: title,
                                            workorder_type:
                                                selectedScheduleId === -1
                                                    ? WorkorderType.ACTION_ITEM
                                                    : WorkorderType.SERVICE_ORDER,
                                            start_date:
                                                startDateIso ?? undefined,
                                            end_date: endDateIso ?? undefined,
                                            assign_to_type: swiperState.position
                                                ? 'user'
                                                : 'vendor',
                                            assign_to_id: swiperState.position
                                                ? swiperState.userId
                                                : swiperState.companyId,
                                        },
                                    }

                                    if (priority) {
                                        request.body.priority = true
                                    }

                                    if (selectedServiceId !== -1) {
                                        request.body.service = selectedServiceId
                                    }

                                    if (selectedScheduleId !== -1) {
                                        request.body.schedule = selectedScheduleId
                                    }

                                    const units: AssignUnitDict[] = []
                                    const invInsIds: number[] = []

                                    inventoryInspections.forEach((invIns) => {
                                        units.push({
                                            unit_id: invIns.unit_id,
                                            service_areas: [invIns.area_id],
                                        })
                                        invInsIds.push(invIns.id)
                                    })

                                    request.body.units = units
                                    request.body.inv_ins_ids = invInsIds

                                    onClickCreate(request)
                                }}
                            >
                                {loading ? (
                                    <CircularProgress
                                        size={20}
                                        style={{ color: 'white' }}
                                    />
                                ) : (
                                    `Create${
                                        inventoryInspections.length > 1
                                            ? ` (${inventoryInspections.length})`
                                            : ''
                                    }`
                                )}
                            </Button>
                        </Container>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}

const ASSIGN_TO_USER = false
const ASSIGN_TO_COMPANY = true
